import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, SearchBar } from 'antd-mobile';

import { useNavbar } from '../../../hooks/use-navbar';
import { useBlockedClients, BlockedClient } from '../../../hooks/use-blocked-clients';
import { faroLogger } from '../../../observability/faro-logger';

const formatAmount = (n: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(n);

const formatDate = (s: string): string => {
  if (!s) {
    return '';
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) {
    return s;
  }
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const ChevronRight: FC = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronDown: FC = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const BlockedClientsPage: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const { enabled, data, isLoading, error } = useBlockedClients();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (navbar) {
      navbar.setTitle('Clients bloqués');
      navbar.setBackArrow(true);
      navbar.setOnBack(() => navigate('/'));
      navbar.setRight();
    }
    faroLogger.info('blocked_clients_opened');
  }, [navbar, navigate]);

  useEffect(() => {
    if (!enabled) {
      navigate('/');
    }
  }, [enabled, navigate]);

  const filtered = useMemo<BlockedClient[]>(() => {
    if (!search.trim()) {
      return data;
    }
    const q = search.trim().toLowerCase();
    return data.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.invoices.some((iv) => iv.number.toLowerCase().includes(q))
    );
  }, [data, search]);

  const totalDue = filtered.reduce((sum, c) => sum + c.totalDue, 0);

  if (!enabled) {
    return null;
  }

  return (
    <div style={{ padding: '14px 14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          background: 'rgba(230,57,70,0.10)',
          border: '1px solid rgba(230,57,70,0.4)',
          borderLeft: '4px solid #E63946',
          borderRadius: 12,
          padding: '12px 14px',
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#FF8A95',
            marginBottom: 4,
          }}
        >
          ⚠ Clients bloqués
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {filtered.length} client{filtered.length > 1 ? 's' : ''} concerné{filtered.length > 1 ? 's' : ''} ·{' '}
          <b style={{ color: '#FF8A95' }}>{formatAmount(totalDue)}</b> dûs au total
        </div>
      </div>

      <SearchBar
        placeholder="Rechercher un client ou un n° de facture"
        value={search}
        onChange={setSearch}
        onClear={() => setSearch('')}
      />

      {isLoading && (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: 13,
          }}
        >
          Chargement…
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '12px 14px',
            background: 'rgba(230,57,70,0.10)',
            border: '1px solid rgba(230,57,70,0.4)',
            borderRadius: 10,
            color: '#FF8A95',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          Impossible de charger la liste : {error.message}
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <Empty description="Aucun client bloqué" />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map((c) => {
          const isOpen = openId === c.id;
          return (
            <div
              key={c.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                borderLeft: '3px solid #E63946',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : c.id)}
                style={{
                  appearance: 'none',
                  background: 'transparent',
                  border: 'none',
                  width: '100%',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                      letterSpacing: '-0.005em',
                      lineHeight: 1.2,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {c.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: 'var(--text-muted)',
                      marginTop: 2,
                    }}
                  >
                    {c.invoices.length} facture{c.invoices.length > 1 ? 's' : ''} ·{' '}
                    <span style={{ color: '#FF8A95', fontWeight: 700 }}>
                      {formatAmount(c.totalDue)}
                    </span>
                  </div>
                </div>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                  {isOpen ? <ChevronDown /> : <ChevronRight />}
                </span>
              </button>
              {isOpen && (
                <div
                  style={{
                    borderTop: '1px solid var(--border-subtle)',
                    padding: '4px 0',
                  }}
                >
                  {c.invoices.length === 0 ? (
                    <div
                      style={{
                        padding: '10px 14px',
                        fontSize: 12,
                        color: 'var(--text-muted)',
                        fontStyle: 'italic',
                        textAlign: 'center',
                      }}
                    >
                      Aucune facture détaillée.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1.4fr 0.9fr 0.9fr',
                          gap: 6,
                          padding: '8px 14px',
                          background: 'var(--bg-card)',
                          fontSize: 9.5,
                          fontWeight: 800,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: 'var(--text-muted)',
                        }}
                      >
                        <span>N° facture</span>
                        <span>Date</span>
                        <span style={{ textAlign: 'right' }}>TTC dû</span>
                      </div>
                      {c.invoices.map((iv, i) => (
                        <div
                          key={`${iv.number}-${i}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1.4fr 0.9fr 0.9fr',
                            gap: 6,
                            padding: '10px 14px',
                            borderTop:
                              i === 0
                                ? 'none'
                                : '1px solid var(--border-subtle)',
                            fontSize: 12.5,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                            {iv.number || '—'}
                          </span>
                          <span>{formatDate(iv.date)}</span>
                          <span
                            style={{
                              textAlign: 'right',
                              color: '#FF8A95',
                              fontWeight: 700,
                            }}
                          >
                            {formatAmount(iv.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BlockedClientsPage;
